import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import GstinField from 'components/common/billing/GstinField';
import GstRegisteredToggle from 'components/common/billing/GstRegisteredToggle';
import StateSelect from 'components/common/billing/StateSelect';
import { findState, isDomesticCountry, stateByCode, usesStatePicker } from 'components/common/billing/taxAddress';
import SectionedModalShell, { ShellBlockers, type ShellSection } from 'components/modals/common/SectionedModalShell';
import { useTaxPack } from 'hooks/api/useTaxPack';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { propertyService } from 'api/services';
import { orgService } from 'api/services/orgService';
import type { Property, CreatePropertyRequest, UpdatePropertyRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PropertyModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: Property | null;
  onSave: () => void;
  onClose: () => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, propertyCode?: string) => void;
}

interface PropertyFormData {
  propertyCode: string;
  propertyName: string;
  propertyType: string;
  starRating: string;
  totalRooms: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  phonePrimary: string;
  phoneSecondary: string;
  email: string;
  website: string;
  gstNumber: string;
  taxStateCode: string;
  /** Kept as stored; one jurisdiction exists today, so it is shown, not chosen. */
  taxJurisdictionCode: string;
  legalName: string;
  isGstRegistered: boolean;
  panNumber: string;
  checkInTime: string;
  checkOutTime: string;
  businessDayCutoff: string;
  currencyCode: string;
  timezone: string;
  entityId: string;
  businessUnitId: string;
  isActive: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: PropertyFormData = {
  propertyCode: '',
  propertyName: '',
  propertyType: '',
  starRating: '',
  totalRooms: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  country: '',
  pincode: '',
  phonePrimary: '',
  phoneSecondary: '',
  email: '',
  website: '',
  gstNumber: '',
  taxStateCode: '',
  taxJurisdictionCode: '',
  legalName: '',
  isGstRegistered: false,
  panNumber: '',
  checkInTime: '',
  checkOutTime: '',
  businessDayCutoff: '03:00',
  currencyCode: '',
  timezone: '',
  entityId: '',
  businessUnitId: '',
  isActive: true,
};

// Sections of the form (slice 3a-ii) and the fields each holds — a validation
// error badges its section and the first one is opened.
type SectionId = 'general' | 'address' | 'operations' | 'tax' | 'accounting';
const SECTION_OF: Record<string, SectionId> = {
  propertyCode: 'general', propertyName: 'general', propertyType: 'general', totalRooms: 'general',
  starRating: 'general', isActive: 'general',
  addressLine1: 'address', addressLine2: 'address', city: 'address', state: 'address', country: 'address',
  pincode: 'address', phonePrimary: 'address', phoneSecondary: 'address', email: 'address', website: 'address',
  checkInTime: 'operations', checkOutTime: 'operations', currencyCode: 'operations', timezone: 'operations',
  businessDayCutoff: 'operations',
  isGstRegistered: 'tax', gstNumber: 'tax', taxStateCode: 'tax', legalName: 'tax', panNumber: 'tax',
  entityId: 'accounting', businessUnitId: 'accounting',
};
const SECTION_TITLES: Record<SectionId, string> = {
  general: 'General', address: 'Address & Contact', operations: 'Operations', tax: 'Billing & Tax', accounting: 'Accounting',
};

/** The form values of a stored property. */
const toFormData = (rec: Property, mode: 'create' | 'edit' | 'copy'): PropertyFormData => ({
  propertyCode: mode === 'copy' ? '' : (rec.propertyCode || ''),
  propertyName: rec.propertyName || '',
  propertyType: rec.propertyType || '',
  starRating: rec.starRating || '',
  totalRooms: rec.totalRooms != null ? String(rec.totalRooms) : '',
  addressLine1: rec.addressLine1 || '',
  addressLine2: rec.addressLine2 || '',
  city: rec.city || '',
  state: rec.state || '',
  country: rec.country || '',
  pincode: rec.pincode || '',
  phonePrimary: rec.phonePrimary || '',
  phoneSecondary: rec.phoneSecondary || '',
  email: rec.email || '',
  website: rec.website || '',
  gstNumber: rec.gstNumber || '',
  taxStateCode: rec.taxStateCode || '',
  taxJurisdictionCode: rec.taxJurisdictionCode || '',
  legalName: rec.legalName || '',
  isGstRegistered: rec.isGstRegistered ?? !!rec.gstNumber,
  panNumber: rec.panNumber || '',
  checkInTime: rec.checkInTime || '',
  checkOutTime: rec.checkOutTime || '',
  businessDayCutoff: (rec.businessDayCutoff || '03:00').slice(0, 5),
  currencyCode: rec.currencyCode || '',
  timezone: rec.timezone || '',
  entityId: rec.entityId || '',
  businessUnitId: rec.businessUnitId || '',
  isActive: rec.isActive,
});

const propertyTypeOptions = [
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Resort', label: 'Resort' },
  { value: 'Serviced Apartment', label: 'Serviced Apartment' },
];

const starRatingOptions = [
  { value: '1 Star', label: '1 Star' },
  { value: '2 Star', label: '2 Star' },
  { value: '3 Star', label: '3 Star' },
  { value: '4 Star', label: '4 Star' },
  { value: '5 Star', label: '5 Star' },
];

const currencyOptions = [
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
];

const timezoneOptions = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'America/New_York', label: 'America/New_York (EST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const PropertyModal: React.FC<PropertyModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<PropertyFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- Sections (slice 3a-ii): left nav, error badges per section ---
  const [activeSection, setActiveSection] = useState<SectionId>('general');

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Effective record ref — starts at prop, gets replaced after a successful refresh from server
  const effectiveRecordRef = useRef<Property | null | undefined>(record);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: PropertyFormData = (mode === 'edit' || mode === 'copy') && rec ? toFormData(rec, mode) : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setActiveSection('general');
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  // Tax settings (slices 3a / 3a-ii): the jurisdiction shown is the tenant's.
  // Registered: a valid GSTIN supplies the state code. Not registered: the address
  // state does. The server stores that code in place of anything typed.
  const { pack: taxPack } = useTaxPack();
  const [gstinState, setGstinState] = useState<string | null>(null);
  const handleGstinChecked = useCallback((placeCode: string | null) => setGstinState(placeCode), []);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.propertyCode?.trim();
      const name = currentFormData.propertyName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add Property (${idx})`;
    } else if (currentMode === 'copy') {
      const code = currentFormData.propertyCode?.trim();
      const name = currentFormData.propertyName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.propertyCode || 'Property'}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? currentFormData.propertyCode : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.propertyCode.trim()) {
      errors.propertyCode = 'Property Code is required';
    } else if (formData.propertyCode.length > 20) {
      errors.propertyCode = 'Property Code cannot exceed 20 characters';
    }

    if (!formData.propertyName.trim()) {
      errors.propertyName = 'Property Name is required';
    } else if (formData.propertyName.length > 100) {
      errors.propertyName = 'Property Name cannot exceed 100 characters';
    }

    if (!formData.propertyType) {
      errors.propertyType = 'Property Type is required';
    }

    if (!formData.totalRooms || parseInt(formData.totalRooms, 10) < 0) {
      errors.totalRooms = 'Total Rooms is required';
    }

    if (!formData.addressLine1.trim()) {
      errors.addressLine1 = 'Address Line 1 is required';
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      errors.state = 'State is required';
    } else if (!formData.isGstRegistered && usesStatePicker(taxPack, formData.country) && !findState(taxPack, formData.state)) {
      errors.state = 'Pick the state from the list — an unregistered property is taxed by it';
    }

    if (formData.isGstRegistered && !formData.gstNumber.trim()) {
      errors.gstNumber = 'GSTIN is required for a registered property';
    }

    if (!formData.pincode.trim()) {
      errors.pincode = 'Pincode is required';
    }

    if (!formData.phonePrimary.trim()) {
      errors.phonePrimary = 'Primary Phone is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setActiveSection(SECTION_OF[Object.keys(errors)[0]] ?? 'general');
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    // Async code uniqueness validation for create/copy modes
    if (mode !== 'edit') {
      try {
        const response = await propertyService.validateCode(formData.propertyCode.trim());
        if (!response.data) {
          setFieldErrors({ propertyCode: 'This property code already exists' });
          setActiveSection('general');
          requestAnimationFrame(() => {
            const el = document.querySelector<HTMLElement>('[data-field="propertyCode"]');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.querySelector<HTMLElement>('input')?.focus();
            }
          });
          return;
        }
      } catch {
        // If validation endpoint fails, proceed with save — backend will reject duplicates
      }
    }

    // Async name uniqueness validation
    {
      try {
        const excludeId = mode === 'edit' && record ? record.id : undefined;
        const response = await propertyService.validateName(formData.propertyName.trim(), excludeId);
        if (!response.data) {
          setFieldErrors({ propertyName: 'This property name already exists' });
          setActiveSection('general');
          requestAnimationFrame(() => {
            const el = document.querySelector<HTMLElement>('[data-field="propertyName"]');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.querySelector<HTMLElement>('input')?.focus();
            }
          });
          return;
        }
      } catch {
        // If validation endpoint fails, proceed with save — backend will reject duplicates
      }
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, record, taxPack]);

  // --- Confirmed save (3-stage: confirm → loading → toast) ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const payload: CreatePropertyRequest | UpdatePropertyRequest = {
        propertyCode: formData.propertyCode.trim(),
        propertyName: formData.propertyName.trim(),
        propertyType: formData.propertyType,
        starRating: formData.starRating || undefined,
        totalRooms: parseInt(formData.totalRooms, 10),
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2 || undefined,
        city: formData.city.trim(),
        state: formData.state.trim(),
        country: formData.country || undefined,
        pincode: formData.pincode.trim(),
        phonePrimary: formData.phonePrimary.trim(),
        phoneSecondary: formData.phoneSecondary || undefined,
        email: formData.email.trim(),
        website: formData.website || undefined,
        // Unregistered: no GSTIN (the server clears it too) and the state comes from
        // the address; the typed code is kept only for a foreign address.
        gstNumber: formData.isGstRegistered ? formData.gstNumber || undefined : undefined,
        isGstRegistered: formData.isGstRegistered,
        taxStateCode: formData.taxStateCode.trim() || undefined,
        taxJurisdictionCode: formData.taxJurisdictionCode.trim() || null,
        legalName: formData.legalName.trim() || null,
        panNumber: formData.panNumber || undefined,
        checkInTime: formData.checkInTime || undefined,
        checkOutTime: formData.checkOutTime || undefined,
        businessDayCutoff: formData.businessDayCutoff || undefined,
        currencyCode: formData.currencyCode || undefined,
        timezone: formData.timezone || undefined,
        entityId: formData.entityId || undefined,
        businessUnitId: formData.businessUnitId || undefined,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await propertyService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdatePropertyRequest);
      } else {
        // create and copy both call create
        await propertyService.create(payload as CreatePropertyRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `"${formData.propertyName}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  // --- Refresh from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff?.id) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await propertyService.getById(eff.id);
      const fresh = response.data;
      effectiveRecordRef.current = fresh;
      const data = toFormData(fresh, 'edit');
      setFormData(data);
      initialSnapshotRef.current = JSON.stringify(data);
      setFieldErrors({});
      setConcurrencyConflict({ open: false, message: '' });
      notificationService.toast.success('Latest version loaded.');
    } catch (refreshErr: any) {
      const msg = refreshErr?.response?.data?.message || refreshErr?.message || 'Failed to load latest version.';
      notificationService.toast.error(msg);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // --- Accounting scope pickers (org backbone lookups) ---
  const loadEntityOptions = useCallback(async (): Promise<SelectOption[]> => {
    const res = await orgService.getEntities();
    return (res.data ?? []).map(e => ({ value: e.id, label: `${e.entityCode} - ${e.entityName}` }));
  }, []);

  const loadBusinessUnitOptions = useCallback(async (): Promise<SelectOption[]> => {
    if (!formData.entityId) return [];
    const res = await orgService.getBusinessUnits(formData.entityId);
    return (res.data ?? []).map(b => ({ value: b.id, label: `${b.unitCode} - ${b.unitName}` }));
  }, [formData.entityId]);

  // --- Code validation on blur ---
  const handleCodeBlur = useCallback(async () => {
    const code = formData.propertyCode.trim();
    if (!code) return;
    try {
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await propertyService.validateCode(code, excludeId);
      if (!response.data) {
        setFieldErrors(prev => ({ ...prev, propertyCode: 'This property code already exists' }));
      }
    } catch {
      // Silently ignore validation errors
    }
  }, [formData.propertyCode, mode, record]);

  // --- Name validation on blur ---
  const handleNameBlur = useCallback(async () => {
    const name = formData.propertyName.trim();
    if (!name) return;
    try {
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await propertyService.validateName(name, excludeId);
      if (!response.data) {
        setFieldErrors(prev => ({ ...prev, propertyName: 'This property name already exists' }));
      }
    } catch {
      // Silently ignore validation errors
    }
  }, [formData.propertyName, mode, record]);

  if (!isOpen) return null;

  const err = getFieldError;
  const set = <K extends keyof PropertyFormData>(field: K, value: PropertyFormData[K]) => {
    clearFieldError(field as string);
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  const errorsIn = (id: SectionId) => Object.keys(fieldErrors).filter(f => (SECTION_OF[f] ?? 'general') === id).length;
  const text = (field: keyof PropertyFormData, label: string, opts: { required?: boolean; placeholder?: string; maxLength?: number; type?: string } = {}) => (
    <div className="form-group" data-field={field}>
      <label className="form-label">{label}{opts.required && <> <span className="text-danger">*</span></>}</label>
      <input
        type={opts.type ?? 'text'}
        className={`form-input${err(field) ? ' form-field-error' : ''}`}
        value={formData[field] as string}
        onChange={(e) => set(field, e.target.value as never)}
        placeholder={opts.placeholder}
        maxLength={opts.maxLength}
      />
      {err(field) && <div className="form-error-message">{err(field)}</div>}
    </div>
  );

  // What the server will store as the property's tax state, said in words.
  const domestic = isDomesticCountry(formData.country, taxPack?.countryCode);
  const addressState = findState(taxPack, formData.state);
  const gstinPlace = stateByCode(taxPack, gstinState);
  const taxStateText = formData.isGstRegistered
    ? (gstinPlace ? `${gstinPlace.placeName} (${gstinPlace.placeCode}) — from the GSTIN` : 'Taken from the GSTIN once it is entered.')
    : domestic
      ? (addressState ? `${addressState.placeName} (${addressState.placeCode}) — from the address state` : `Pick the state on ${SECTION_TITLES.address}.`)
      : null; // foreign address: the typed code below
  const stateMismatch = formData.isGstRegistered && gstinPlace && addressState && gstinPlace.placeCode !== addressState.placeCode;

  const sections: ShellSection[] = [
    {
      id: 'general',
      title: SECTION_TITLES.general,
      errorCount: errorsIn('general'),
      content: (
        <div className="form-grid-2">
          <div className="form-group" data-field="propertyCode">
            <label className="form-label">Property Code <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${err('propertyCode') ? ' form-field-error' : ''}`}
              value={formData.propertyCode}
              onChange={(e) => set('propertyCode', e.target.value)}
              onBlur={handleCodeBlur}
              placeholder="e.g., PROP001"
              maxLength={20}
              readOnly={mode === 'edit'}
              style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : undefined}
              autoFocus={mode === 'create' || mode === 'copy'}
            />
            {err('propertyCode') && <div className="form-error-message">{err('propertyCode')}</div>}
          </div>
          <div className="form-group" data-field="propertyName">
            <label className="form-label">Property Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${err('propertyName') ? ' form-field-error' : ''}`}
              value={formData.propertyName}
              onChange={(e) => set('propertyName', e.target.value)}
              onBlur={handleNameBlur}
              placeholder="e.g., Grand Hotel"
              maxLength={100}
            />
            {err('propertyName') && <div className="form-error-message">{err('propertyName')}</div>}
          </div>
          <div className="form-group" data-field="propertyType">
            <SyutSelect
              label="Property Type"
              required
              options={propertyTypeOptions}
              value={formData.propertyType}
              onChange={(val) => set('propertyType', (val as string) || '')}
              placeholder="Select type..."
              isSearchable
              isClearable
              size="md"
              error={err('propertyType')}
            />
          </div>
          {text('totalRooms', 'Total Rooms', { required: true, type: 'number', placeholder: 'e.g., 120' })}
          <div className="form-group" data-field="starRating">
            <SyutSelect
              label="Star Rating"
              options={starRatingOptions}
              value={formData.starRating}
              onChange={(val) => set('starRating', (val as string) || '')}
              placeholder="Select rating..."
              isClearable
              size="md"
            />
          </div>
          <div className="form-group" data-field="isActive">
            <SyutSelect
              label="Status"
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
              value={String(formData.isActive)}
              onChange={(val) => set('isActive', val === 'true')}
              isClearable={false}
              size="md"
            />
          </div>
        </div>
      ),
    },
    {
      id: 'address',
      title: SECTION_TITLES.address,
      errorCount: errorsIn('address'),
      content: (
        <>
          <div className="form-grid-2">
            {text('addressLine1', 'Address Line 1', { required: true, placeholder: 'Street address...', maxLength: 200 })}
            {text('addressLine2', 'Address Line 2', { placeholder: 'Area, landmark...', maxLength: 200 })}
          </div>
          <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
            {text('city', 'City', { required: true, placeholder: 'City', maxLength: 100 })}
            <StateSelect
              value={formData.state}
              onChange={(v) => set('state', v)}
              pack={taxPack}
              country={formData.country}
              required
              error={err('state')}
            />
            {text('pincode', 'Pincode', { required: true, placeholder: 'Pincode', maxLength: 10 })}
            {text('country', 'Country', { placeholder: 'India (empty = India)', maxLength: 100 })}
          </div>
          <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
            {text('phonePrimary', 'Phone (Primary)', { required: true, placeholder: 'Primary phone', maxLength: 20 })}
            {text('phoneSecondary', 'Phone (Secondary)', { placeholder: 'Secondary phone', maxLength: 20 })}
            {text('email', 'Email', { required: true, type: 'email', placeholder: 'email@example.com', maxLength: 255 })}
            {text('website', 'Website', { placeholder: 'www.example.com', maxLength: 255 })}
          </div>
        </>
      ),
    },
    {
      id: 'operations',
      title: SECTION_TITLES.operations,
      errorCount: errorsIn('operations'),
      content: (
        <div className="form-grid-2">
          {text('checkInTime', 'Check-in Time', { type: 'time' })}
          {text('checkOutTime', 'Check-out Time', { type: 'time' })}
          <div className="form-group" data-field="businessDayCutoff">
            <label className="form-label">Business day cutoff</label>
            <input type="time" className={`form-input${err('businessDayCutoff') ? ' form-field-error' : ''}`}
              value={formData.businessDayCutoff} max="12:00"
              onChange={(e) => set('businessDayCutoff', e.target.value)} />
            {err('businessDayCutoff')
              ? <div className="form-error-message">{err('businessDayCutoff')}</div>
              : <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  The business day starts here (00:00–12:00). With 03:00, a charge at 01:30 belongs to the previous day.
                </div>}
          </div>
          <div className="form-group" data-field="currencyCode">
            <SyutSelect
              label="Currency"
              options={currencyOptions}
              value={formData.currencyCode}
              onChange={(val) => set('currencyCode', (val as string) || '')}
              placeholder="Select..."
              isClearable
              size="md"
            />
          </div>
          <div className="form-group" data-field="timezone">
            <SyutSelect
              label="Timezone"
              options={timezoneOptions}
              value={formData.timezone}
              onChange={(val) => set('timezone', (val as string) || '')}
              placeholder="Select..."
              isSearchable
              isClearable
              size="md"
            />
          </div>
        </div>
      ),
    },
    {
      id: 'tax',
      title: SECTION_TITLES.tax,
      errorCount: errorsIn('tax'),
      content: (
        <>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Tax jurisdiction</label>
              <input type="text" className="form-input" readOnly
                value={formData.taxJurisdictionCode || (taxPack ? `${taxPack.jurisdictionCode} (default)` : 'Tenant default')}
                style={{ background: 'var(--bg-secondary)' }}
                title="The tenant's tax jurisdiction. Only one is set up, so it is not chosen here." />
            </div>
            <GstRegisteredToggle value={formData.isGstRegistered} onChange={(v) => set('isGstRegistered', v)} disabled={isSaving} />
            {formData.isGstRegistered && (
              <GstinField
                label="GSTIN *"
                value={formData.gstNumber}
                onChange={(gstNumber) => set('gstNumber', gstNumber)}
                onChecked={handleGstinChecked}
                error={err('gstNumber')}
                emptyHint="Required for a registered property. The tax state is read from it."
              />
            )}
            {taxStateText !== null ? (
              <div className="form-group">
                <label className="form-label">Tax state</label>
                <div style={{ minHeight: '2.25rem', display: 'flex', alignItems: 'center', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                  {taxStateText}
                </div>
              </div>
            ) : (
              <div className="form-group" data-field="taxStateCode">
                <label className="form-label">Tax State Code</label>
                <input type="text" className={`form-input${err('taxStateCode') ? ' form-field-error' : ''}`}
                  value={formData.taxStateCode}
                  onChange={(e) => set('taxStateCode', e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase())}
                  placeholder="e.g., 96" maxLength={5}
                  title="Statutory state code for tax — only for an address outside the country." />
                {err('taxStateCode') && <div className="form-error-message">{err('taxStateCode')}</div>}
              </div>
            )}
            {text('legalName', formData.isGstRegistered ? 'Legal name (as registered)' : 'Legal name', { placeholder: 'Printed on tax invoices; empty = property name', maxLength: 200 })}
            {text('panNumber', 'PAN Number', { placeholder: 'PAN number', maxLength: 10 })}
          </div>
          {stateMismatch && (
            <div style={{
              marginTop: '0.25rem', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem',
              background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)',
            }}>
              The GSTIN is registered in {gstinPlace!.placeName}, but the address state is {addressState!.placeName}.
              Invoices use the GSTIN state — check the GSTIN belongs to this property's registration.
            </div>
          )}
        </>
      ),
    },
    {
      id: 'accounting',
      title: SECTION_TITLES.accounting,
      errorCount: errorsIn('accounting'),
      badge: formData.entityId ? 'ok' : 'warn',
      content: (
        <>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>
            The Fairbooks entity and business unit this property posts into. Required when Fairbooks posting is enabled — without it, advances park for manual review.
          </p>
          {!formData.entityId && (
            <div style={{
              padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.8rem',
              background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)',
            }}>
              Receipt and invoice numbers follow the entity's financial year. Until an entity is set, payments are
              saved with their receipt number pending — issue them from Document Series once it is linked.
            </div>
          )}
          <div className="form-grid-2">
            <div className="form-group" data-field="entityId">
              <SyutSelect
                label="Accounting Entity"
                isAsync
                loadOptions={loadEntityOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.entityId}
                onChange={(val) => setFormData(prev => ({
                  ...prev,
                  entityId: (val as string) || '',
                  // A unit only means something inside its entity — reselect it.
                  businessUnitId: '',
                }))}
                placeholder="Not wired to accounting"
                isClearable
                size="md"
                menuPortalTarget={document.body}
                seedOptions={record?.entityId && record.entityName ? [{ value: record.entityId, label: record.entityName }] : undefined}
              />
            </div>
            <div className="form-group" data-field="businessUnitId">
              <SyutSelect
                key={formData.entityId || 'no-entity'}
                label="Business Unit"
                isAsync
                loadOptions={loadBusinessUnitOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.businessUnitId}
                onChange={(val) => set('businessUnitId', (val as string) || '')}
                placeholder={formData.entityId ? 'Select business unit...' : 'Select the entity first'}
                isClearable
                isDisabled={!formData.entityId}
                size="md"
                menuPortalTarget={document.body}
                seedOptions={record?.businessUnitId && record.businessUnitName ? [{ value: record.businessUnitId, label: record.businessUnitName }] : undefined}
              />
            </div>
          </div>
        </>
      ),
    },
  ];

  const blockers = (Object.keys(SECTION_TITLES) as SectionId[])
    .filter(id => errorsIn(id) > 0)
    .map(id => ({ label: `${SECTION_TITLES[id]}: ${errorsIn(id)} to fix`, sectionId: id }));

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <SectionedModalShell
        title={mode === 'create' ? 'Add Property' : mode === 'copy' ? 'Copy Property' : 'Edit Property'}
        summary={formData.propertyName || formData.propertyCode ? (
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{formData.propertyName || '—'}</strong>
            {formData.propertyCode && <> · {formData.propertyCode}</>}
            {formData.isGstRegistered && formData.gstNumber && <> · GSTIN {formData.gstNumber}</>}
          </span>
        ) : undefined}
        sections={sections}
        activeSection={activeSection}
        onSectionChange={(id) => setActiveSection(id as SectionId)}
        onClose={handleClose}
        onMinimize={tabId ? handleMinimize : undefined}
        busy={isSaving}
        loading={{ active: isSaving, label: mode === 'edit' ? 'Saving...' : 'Creating...' }}
        maxWidth="960px"
        footer={
          <>
            <ShellBlockers blockers={blockers} onJump={(id) => setActiveSection(id as SectionId)} />
            <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving || (mode === 'edit' && !isDirty)}
              title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
            >
              <IconSystem.save size={16} />
              {mode === 'edit' ? 'Save Changes' : 'Create Property'}
            </button>
          </>
        }
      />
      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the property "${formData.propertyName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default PropertyModal;
